import { motion, useInView } from "framer-motion";
import { useRef } from "react";

const STEPS = [
  {
    n: "01",
    title: "Site Audit",
    time: "24 hrs",
    body: "Photo walk-through of your property. We map every surface, substrate, and access constraint before touching a single square foot.",
  },
  {
    n: "02",
    title: "Scoped Brief",
    time: "48 hrs",
    body: "A written proposal with cadence, equipment, crew count, and photo debrief protocol. No verbal handshakes.",
  },
  {
    n: "03",
    title: "Execution",
    time: "Ongoing",
    body: "Uniformed crew, calibrated equipment, insured to $2M. Every visit is briefed pre-shift and debriefed post-shift.",
  },
  {
    n: "04",
    title: "Photo Debrief",
    time: "Same day",
    body: "Before/after imagery and a written report land in your inbox within hours of completion. Insurance-grade documentation.",
  },
];

export default function ProcessTimeline() {
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, amount: 0.2 });

  return (
    <section ref={sectionRef} className="py-32 md:py-40 relative" style={{ background: "var(--bg)" }} data-testid="process-timeline">
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl mb-20"
        >
          <div className="overline mb-6">The Method</div>
          <h2 className="font-display text-5xl md:text-7xl" style={{ letterSpacing: "-0.035em", lineHeight: 0.95 }}>
            From <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>contact</span> to <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>completion.</span>
          </h2>
          <p className="mt-8 font-body text-lg md:text-xl max-w-2xl" style={{ color: "var(--text-2)", lineHeight: 1.6 }}>
            Four steps. Documented. Repeated across every property, every visit.
          </p>
        </motion.div>

        {/* Timeline rail */}
        <div className="relative">
          {/* Vertical rail (desktop) */}
          <div className="hidden md:block absolute left-[7%] top-4 bottom-4 w-px" style={{ background: "linear-gradient(180deg, transparent, var(--line-gold) 15%, var(--line-gold) 85%, transparent)" }} />

          <div className="space-y-8 md:space-y-14">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, x: -30 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.9, delay: 0.1 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                className="grid md:grid-cols-12 gap-6 md:gap-10 items-start relative"
                data-testid={`process-step-${s.n}`}
              >
                {/* Step number node */}
                <div className="md:col-span-2 flex md:justify-center items-center">
                  <div className="relative flex items-center gap-4">
                    <div
                      className="w-16 h-16 flex items-center justify-center font-display text-2xl relative z-10"
                      style={{
                        background: "var(--bg)",
                        border: "1px solid var(--gold)",
                        color: "var(--gold)",
                        borderRadius: "var(--r-full)",
                        boxShadow: "0 0 32px rgba(198,168,124,0.18), inset 0 0 12px rgba(198,168,124,0.05)",
                      }}
                    >
                      {s.n}
                    </div>
                    <div className="md:hidden font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--text-3)" }}>
                      {s.time}
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="md:col-span-9 glass-card p-8 md:p-10">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display text-3xl md:text-4xl" style={{ letterSpacing: "-0.02em", color: "var(--text)" }}>{s.title}</h3>
                    <div className="hidden md:flex items-center gap-2 pill">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#4ade80" }} />
                      {s.time}
                    </div>
                  </div>
                  <p className="font-body text-base md:text-lg max-w-2xl leading-relaxed" style={{ color: "var(--text-2)" }}>
                    {s.body}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
